import {useEffect, useState} from 'react';
import {Box, Stack} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import {ReactComponent as CaretUp} from '../../../../assets/images/caret-up.svg';
import {ReactComponent as CaretDown} from '../../../../assets/images/caret-down.svg';
import {ReactComponent as CloseToast} from '../../../../assets/images/close-toast.svg';
import {t} from 'i18next';
import {GridColDef, GridRenderCellParams} from '@mui/x-data-grid';
import {
  usePostIngestionCoping,
  usePostIngestionReRun,
} from '../../../../hooks/ingestions/useIngestions';
import {TOAST_DURATION, ToastType} from '../../../../shared/constants';
import ArrToast from '../../../common/arr-toast/ArrToast';
import IngestionErrors from '../../ingestion-errors/IngestionErrors';
import dayjs from 'dayjs';
import {ingestionDetailsModel} from '../../../../api/types/ingestionApi.type';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';

type IngestionHistoryItem = {
  startDate?: string;
  endDate?: string;
  completed?: number;
  errorCount?: number;
};

type IngestionProcessSectionProps = {
  name: 'cataloging' | 'copying';
  projectId: number;
  ingestionId: number;
  ingestionState: string;
  title: string;
  runButton?: boolean;
  onIngestionProcessSuccess?: () => void;
  canReRun?: boolean;
  ingestionName?: string;
  canShowLabels: boolean;
  runButtonAriaLabel?: string;
  ingestionModel?: ingestionDetailsModel;
  onDoneSuccess?: () => void;
};
interface ToastDetails {
  toastType: ToastType;
  toastMessage: string;
}

const INGESTION_FAILED = 'FAILED';

const IngestionProcessSection = ({
  name,
  projectId,
  ingestionId,
  ingestionState,
  title,
  runButton = true,
  canReRun = false,
  onIngestionProcessSuccess,
  ingestionName,
  canShowLabels = true,
  runButtonAriaLabel = `${t('ingestions.details.run')} ${name}`,
  ingestionModel,
}: IngestionProcessSectionProps) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showToastOnErrorModal, setShowToastOnErrorModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<[ToastType]>([ToastType.Success]);

  useEffect(() => {
    setShowHistory(false);
  }, [ingestionId]);

  const {mutate: runIngestion, isError} = usePostIngestionReRun();
  const {mutate: copyIngestion, isError: isCopyError} =
    usePostIngestionCoping();

  const handleReRun = () => {
    if (name === 'cataloging') {
      runIngestion(
        {projectId, ingestionId},
        {
          onSuccess: () => {
            onIngestionProcessSuccess?.();
            setShowToast(true);
          },
          onError: () => {
            setShowToast(true);
          },
        }
      );
    } else {
      copyIngestion(
        {
          projectId,
          ingestionId,
          isReRun:
            ingestionState?.toUpperCase() === INGESTION_FAILED ? true : false,
        },
        {
          onSuccess: () => {
            onIngestionProcessSuccess?.();
            setShowToast(true);
          },
          onError: () => {
            setShowToast(true);
          },
        }
      );
    }
  };
  // ======== Columns ========
  const catalogingColumns: GridColDef[] = [
    {field: 'fileType', headerName: '', flex: 2, sortable: false},
    {
      field: 'sourceCount',
      headerName: t('ingestions.details.sourceCount'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'catalogedCount',
      headerName: t('ingestions.details.catalogedCount'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'errorCount',
      headerName: t('ingestions.details.errors'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const {errorCount} = params.row;
        return (
          <span
            className={errorCount === 0 ? '' : 'ingestion-errors-cell'}
            onClick={
              errorCount === 0 ? undefined : () => setShowErrorModal(true)
            }
            data-testid={`${params.id}-ingestion-process-catalog-error-count`}
          >
            {errorCount}
          </span>
        );
      },
    },
  ];

  const copyingColumns: GridColDef[] = [
    {field: 'localizedFileType', headerName: '', flex: 2, sortable: false},
    {
      field: 'sourceCount',
      headerName: t('ingestions.details.sourceCount'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'copiedCount',
      headerName: t('ingestions.details.copiedCount'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'errorCount',
      headerName: t('ingestions.details.errors'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const {errorCount} = params.row;
        return (
          <span
            className={errorCount === 0 ? '' : 'ingestion-errors-cell'}
            onClick={
              errorCount === 0 ? undefined : () => setShowErrorModal(true)
            }
            data-testid={`${params.id}-ingestion-process-copy-error-count`}
          >
            {errorCount}
          </span>
        );
      },
    },
    {
      field: 'missedDocCount',
      headerName: t('ingestions.details.missedDocCount'),
      flex: 1,
      sortable: false,
    },
  ];

  // ======== Rows ========
  const catalogingRows = ingestionModel?.cataloging
    ? [
        {
          id: 1,
          fileType: ingestionModel.cataloging.fileType,
          sourceCount: ingestionModel.cataloging.sourceCount,
          catalogedCount: ingestionModel.cataloging.catalogedCount,
          errorCount: ingestionModel.cataloging.errorCount,
        },
      ]
    : [];

  const copyingRows =
    ingestionModel?.copying?.ingestioncounts?.map((item, index) => ({
      ...item,
      id: index,
    })) ?? [];

  const catalogingDetails = [
    {
      label: t('ingestions.details.percentageCompleted'),
      value: ingestionModel?.cataloging?.status,
    },
    {
      label: t('ingestions.details.startDate'),
      value: ingestionModel?.cataloging?.startDate
        ? dayjs(ingestionModel.cataloging.startDate).format(
            'YYYY/MM/DD hh:mm:ss'
          )
        : '',
    },
    {
      label: t('ingestions.details.endDate'),
      value: ingestionModel?.cataloging?.endDate
        ? dayjs(ingestionModel.cataloging.endDate).format('YYYY/MM/DD hh:mm:ss')
        : '',
    },
    {
      label: t('ingestions.details.documentsCataloged'),
      value: {
        main:
          ingestionModel?.cataloging?.catalogedCount?.toLocaleString() ?? '0',
        errorCount: ingestionModel?.cataloging?.errorCount ?? 0,
      },
    },
  ];

  const latestCopyingHistory = ingestionModel?.copying?.copyingHistory
    ?.slice()
    .sort(
      (a, b) =>
        Date.parse(b.endDate || b.startDate || '') -
        Date.parse(a.endDate || a.startDate || '')
    )[0];

  const copyingDetails = [
    {
      label: t('ingestions.details.percentageCompleted'),
      value: ingestionModel?.copying?.status,
    },
    {
      label: t('ingestions.details.startDate'),
      value: ingestionModel?.copying?.startDate
        ? dayjs(ingestionModel.copying.startDate).format('YYYY/MM/DD hh:mm:ss')
        : '',
    },
    {
      label: t('ingestions.details.endDate'),
      value: ingestionModel?.copying?.endDate
        ? dayjs(ingestionModel.copying.endDate).format('YYYY/MM/DD hh:mm:ss')
        : '',
    },
    {
      label: t('ingestions.details.documentsFilesCopied'),
      value: {
        main: (latestCopyingHistory?.copiedCount ?? 0).toLocaleString(),
        errorCount: ingestionModel?.copying?.totalErrors ?? 0,
      },
    },
  ];

  const showToastOnClose = (toastDetails: ToastDetails) => {
    setShowToastOnErrorModal(true);
    setToastType([toastDetails.toastType]);
    setToastMessage(toastDetails.toastMessage);
  };

  const rows = name === 'cataloging' ? catalogingRows : copyingRows;
  const columns = name === 'cataloging' ? catalogingColumns : copyingColumns;
  const details = name === 'cataloging' ? catalogingDetails : copyingDetails;
  const historyLabel =
    name === 'cataloging'
      ? t('ingestions.details.showCatalogingHistory')
      : t('ingestions.details.showCopyingHistory');
  const historyItems: IngestionHistoryItem[] =
    name === 'cataloging'
      ? (ingestionModel?.cataloging?.catalogingHistory?.map((item) => ({
          startDate: item.startDate,
          endDate: item.endDate,
          completed: item.copiedCount,
          errorCount: item.errorCount,
        })) ?? [])
      : (ingestionModel?.copying?.copyingHistory?.map((item) => ({
          startDate: item.startDate,
          endDate: item.endDate,
          completed: item.copiedCount,
          errorCount: item.errorCount,
        })) ?? []);

  return (
    <>
      <Box className="ingestion-process-section">
        {/* Details Section */}
        <Box className="details-section">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb="1rem"
          >
            <Box className="section-title" tabIndex={0}>
              {title}
            </Box>
            {runButton && (
              <ArrButton
                disabled={!canReRun}
                onClick={handleReRun}
                aria-label={runButtonAriaLabel}
                size="small"
                dataTestId="ingestion-process-section-run-button"
              >
                {t('ingestions.details.run')}
              </ArrButton>
            )}
          </Stack>

          {details.map((detail, index) => (
            <Box key={index} className="detail-row">
              <Box className="detail-label">{detail.label}:</Box>
              <Box className="detail-value">
                {typeof detail.value === 'object' && detail.value !== null ? (
                  <>
                    <span
                      data-testid={`ingestion-process-section-value-${index}`}
                    >
                      {detail.value.main}
                    </span>
                    &nbsp;
                    {detail.value.errorCount !== undefined && (
                      <span
                        role="link"
                        tabIndex={0}
                        className="error-count"
                        style={{
                          cursor:
                            detail.value.errorCount === 0
                              ? 'default'
                              : 'pointer',
                        }}
                        onClick={
                          detail.value.errorCount === 0
                            ? () => {}
                            : () => setShowErrorModal(true)
                        }
                        data-testid={`${detail.label}-detail-value`}
                      >
                        ({t('ingestions.details.errors')}:{' '}
                        {detail.value.errorCount})
                      </span>
                    )}
                  </>
                ) : (
                  canShowLabels && (detail.value ?? '')
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {showErrorModal && (
          <IngestionErrors
            showModal={showErrorModal}
            onClose={() => setShowErrorModal(false)}
            ingestionName={ingestionName}
            projectId={projectId}
            ingestionId={ingestionId}
            ingestionDetailsModel={ingestionModel}
            ingestionState={ingestionState}
            name={name}
            onCloseModal={showToastOnClose}
            onDoneSuccess={() => onIngestionProcessSuccess?.()}
          />
        )}

        {/* History Section */}
        <Box className="history-section">
          <ArrButton
            className="pl-null"
            size="small"
            onClick={() => setShowHistory((prev) => !prev)}
            variant="text"
            sx={{border: 0, '&:focus': {border: 0}}}
            endIcon={showHistory ? <CaretUp /> : <CaretDown />}
            dataTestId="ingestion-process-history-toggle-button"
          >
            {historyLabel}
          </ArrButton>

          {showHistory && historyItems.length > 0 && (
            <Box>
              {historyItems.map((item, index) => (
                <Box className="details-section" key={index}>
                  {[
                    {
                      label: t('ingestions.details.startDate'),
                      value: item.startDate
                        ? dayjs(item.startDate).format('YYYY/MM/DD hh:mm:ss')
                        : '',
                    },
                    {
                      label: t('ingestions.details.endDate'),
                      value: item.endDate
                        ? dayjs(item.endDate).format('YYYY/MM/DD hh:mm:ss')
                        : '',
                    },
                    {
                      label: t('ingestions.details.completed'),
                      value: item.completed ?? '',
                    },
                    {
                      label: t('ingestions.details.errorCount'),
                      value: item.errorCount ?? '',
                    },
                  ].map((f, idx) => (
                    <Box key={idx} className="detail-row">
                      <Box className="detail-label">{f.label}:</Box>
                      <Box className="detail-value">{f.value}</Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}

          <Box className={`process-section-grid-${name}`}>
            {/* remove it once rnfv is live */}
            <ArrDataGrid
              columns={columns}
              rows={rows}
              getRowClassName={(params) => {
                const fileType = String(params.row.fileType ?? '')
                  .trim()
                  .toLocaleLowerCase();
                const localizedFileType = String(
                  params.row.localizedFileType ?? ''
                )
                  .trim()
                  .toLocaleLowerCase();
                return fileType === 'rnfv' ||
                  localizedFileType === 'redacted native'
                  ? 'hidden-redacted-native-row'
                  : '';
              }}
            />
            {/* <ArrDataGrid columns={columns} rows={rows} /> */}
          </Box>
        </Box>

        {/* Toast Notification */}
        {showToast && (
          <ArrToast
            id="toast-ing-details"
            title={isError || isCopyError ? 'Error' : 'Success'}
            message={
              isError || isCopyError
                ? t('ingestions.details.toastFail')
                : t('ingestions.details.toastSucces', {name})
            }
            type={isError || isCopyError ? 'error' : 'success'}
            duration={TOAST_DURATION}
            onClose={() => setShowToast(false)}
            dismissable
            closeIcon={<CloseToast />}
            position="bottom-right"
          />
        )}
      </Box>
      {showToastOnErrorModal && (
        <ArrToastNotification
          message={toastMessage}
          status={toastType}
          onClose={() => setShowToastOnErrorModal(false)}
        />
      )}
    </>
  );
};

export default IngestionProcessSection;
